import { ArrowRight, Bike, Clock3, ShieldCheck, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DishImage } from '@/components/DishImage';
import { useMealPeriod } from '@/hooks/useMealPeriod';
import { formatNairobiTime, getDeliveryEstimate } from '@/lib/time';
import { formatKES } from '@/lib/format';
import { CATEGORY_META, HERO_IMAGE, MENU } from '@/lib/menu';

function scrollToMenu() {
  document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function Hero() {
  const info = useMealPeriod();
  const featured = MENU.find((m) => m.id === 'ugali-nyama')!;
  const categoryLabel = CATEGORY_META[info.menuCategory].label.toLowerCase();

  return (
    <section id="top" className="relative isolate overflow-hidden">
      {/* Ambient background wash */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-32 -top-32 size-[28rem] rounded-full bg-marigold/20 blur-3xl" />
        <div className="absolute -right-24 top-24 size-[24rem] rounded-full bg-ember/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 size-[20rem] rounded-full bg-leaf/10 blur-3xl" />
      </div>

      <div className="container grid items-center gap-12 py-14 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-8">
        {/* Copy */}
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2 shadow-sm">
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex size-full animate-ping-slow rounded-full bg-leaf" />
              <span className="relative inline-flex size-2.5 rounded-full bg-leaf" />
            </span>
            <span className="text-sm font-semibold">{info.headline}</span>
            <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
              <Clock3 className="size-3" />
              {formatNairobiTime()} EAT
            </span>
          </div>

          <h1 className="mt-6 font-display text-5xl font-black leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            {info.greeting}!{' '}
            <span className="italic text-primary">Real Kenyan food,</span>{' '}
            hot to your door.
          </h1>

          <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
            {info.blurb} Order in seconds, pay instantly with{' '}
            <span className="font-semibold text-foreground">M&#8209;Pesa</span> or{' '}
            <span className="font-semibold text-foreground">Tando</span>, and we deliver
            anywhere in Nairobi in 30–45 minutes.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              onClick={scrollToMenu}
              className="rounded-full px-7 text-base font-bold shadow-lg shadow-primary/30"
            >
              Order {categoryLabel} now
              <ArrowRight className="size-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={scrollToMenu}
              className="rounded-full border-2 px-7 text-base font-bold"
            >
              Browse full menu
            </Button>
          </div>

          <dl className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
            <div className="flex items-center gap-2.5">
              <span className="flex size-10 items-center justify-center rounded-full bg-marigold/20 text-accent-foreground">
                <Star className="size-4 fill-marigold text-marigold" />
              </span>
              <div className="leading-tight">
                <dt className="text-base font-bold">4.9 / 5</dt>
                <dd className="text-xs text-muted-foreground">12k+ reviews</dd>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex size-10 items-center justify-center rounded-full bg-leaf/15 text-secondary">
                <Bike className="size-4" />
              </span>
              <div className="leading-tight">
                <dt className="text-base font-bold">30–45 min</dt>
                <dd className="text-xs text-muted-foreground">across Nairobi</dd>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex size-10 items-center justify-center rounded-full bg-ember/15 text-primary">
                <ShieldCheck className="size-4" />
              </span>
              <div className="leading-tight">
                <dt className="text-base font-bold">Secure pay</dt>
                <dd className="text-xs text-muted-foreground">M-Pesa &amp; Tando</dd>
              </div>
            </div>
          </dl>
        </div>

        {/* Visual */}
        <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
          <div className="relative overflow-hidden rounded-[2rem] shadow-2xl shadow-ember/20 ring-1 ring-border">
            <DishImage
              src={HERO_IMAGE}
              alt="Nyama choma grilling over a charcoal jiko"
              className="aspect-[4/5] w-full sm:aspect-[5/4] lg:aspect-[4/5]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
            <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 text-white">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/75">
                  Fresh off the jiko
                </p>
                <p className="mt-1 font-display text-2xl font-bold">Charcoal-grilled daily</p>
              </div>
            </div>
          </div>

          {/* Floating dish card */}
          <div className="absolute -left-3 top-8 motion-safe:animate-float sm:-left-8">
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-card/95 p-3 pr-5 shadow-xl backdrop-blur">
              <DishImage
                src={featured.image}
                alt={featured.name}
                className="size-14 rounded-xl"
              />
              <div className="leading-tight">
                <p className="text-sm font-bold">{featured.name}</p>
                <p className="mt-0.5 text-sm font-semibold text-primary">{formatKES(featured.price)}</p>
              </div>
            </div>
          </div>

          {/* Floating ETA card */}
          <div className="absolute -bottom-5 right-4 motion-safe:animate-float sm:right-8" style={{ animationDelay: '1.2s' }}>
            <div className="flex items-center gap-3 rounded-2xl border border-border bg-card/95 p-3 pr-5 shadow-xl backdrop-blur">
              <span className="flex size-11 items-center justify-center rounded-xl bg-leaf text-secondary-foreground">
                <Bike className="size-5" />
              </span>
              <div className="leading-tight">
                <p className="text-sm font-bold">Rider on the way</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Arrives {getDeliveryEstimate()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
